import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getDriverPhones, updateDriverPhone } from "../../api/helpdeskDrivers";
import { invalidateDriverCache } from "./DriverSelector";
import { driverMessagingErrorText } from "../../utils/driverMessagingErrors";
import { isPlausiblePhone } from "../../utils/driverPhone";
import { cn } from "../../utils/cn";

// Mismo input que el resto del panel: _modern-forms.scss estiliza
// input[type="text"] por elemento y le gana a las utilidades sin `!`.
const INPUT_CLS =
  "w-full !h-11 md:!h-8 !px-2.5 !py-0 !text-base md:!text-sm !rounded-md !border !border-border !bg-bg text-fg placeholder:!text-fg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50";
const BTN =
  "inline-flex items-center justify-center h-11 md:h-8 px-3 rounded-md text-xs font-medium border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

/**
 * Edita el teléfono REGISTRADO de un repartidor (PATCH users/drivers/:id/phone).
 * Acepta número nacional o con "+": el backend lo normaliza al país del
 * operador. Lo usan el modal "Escribir a repartidor" y el panel de contacto.
 *
 * Props:
 *   - driverId
 *   - onSaved(phone)  teléfono E.164 ya normalizado por el backend
 *   - onCancel()
 */
export default function DriverPhoneEditor({ driverId, onSaved, onCancel }) {
  const { t } = useTranslation();
  const inputId = useId();
  const hintId = useId();
  const errorId = useId();
  const inputRef = useRef(null);
  const savingRef = useRef(false);
  const [value, setValue] = useState("");
  const [currentPhone, setCurrentPhone] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // El teléfono vigente se muestra solo como referencia; si no carga, el
  // formulario sigue siendo usable.
  useEffect(() => {
    let cancelled = false;
    getDriverPhones(driverId).then((result) => {
      if (cancelled || result.error) return;
      const registered = (result.data?.phones || []).find((p) => p.source === "registered");
      setCurrentPhone(registered?.phone || null);
    });
    return () => {
      cancelled = true;
    };
  }, [driverId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (savingRef.current) return;
    const phone = value.trim();
    if (!isPlausiblePhone(phone)) {
      setError(t("El teléfono no es válido. Revisa el número e incluye el código de país."));
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    const result = await updateDriverPhone(driverId, phone);
    savingRef.current = false;
    setSaving(false);
    if (result.error) {
      setError(driverMessagingErrorText(result.error, t));
      return;
    }
    // El selector de repartidores muestra los últimos 4 dígitos en su caché.
    invalidateDriverCache();
    onSaved?.(result.data?.phone || phone);
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-2">
      <label htmlFor={inputId} className="block text-xs font-medium text-fg">
        {t("Nuevo teléfono registrado")}
      </label>
      <p id={hintId} className="text-xs text-fg-muted leading-relaxed">
        {currentPhone
          ? t("Teléfono actual: {{phone}}. Cambiarlo actualiza la ficha del repartidor.").replace(
              "{{phone}}",
              currentPhone,
            )
          : t("Cambiarlo actualiza la ficha del repartidor.")}
      </p>
      <input
        id={inputId}
        ref={inputRef}
        type="text"
        inputMode="tel"
        autoComplete="off"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          if (error) setError("");
        }}
        disabled={saving}
        placeholder={t("+56 9 1234 5678")}
        aria-describedby={error ? `${hintId} ${errorId}` : hintId}
        aria-invalid={error ? true : undefined}
        className={INPUT_CLS}
      />
      {error && (
        <p id={errorId} role="alert" className="text-xs text-status-error">
          {error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saving || !value.trim()}
          aria-busy={saving}
          // hd-brand-solid: white on brand-500 is 2,80:1 (helpdeskMobile.scss).
          className={cn(BTN, "hd-brand-solid border-transparent bg-brand-500 text-white hover:bg-brand-600")}
        >
          {saving ? t("Guardando…") : t("Guardar teléfono")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className={cn(BTN, "border-border bg-transparent text-fg hover:bg-bg-subtle")}
        >
          {t("Cancelar")}
        </button>
      </div>
    </form>
  );
}
